// frontend/src/services/usuariosService.js

import api from './api'; // Tu instancia de axios configurada

const usuariosService = {
  /**
   * Obtiene la lista de usuarios de una cuenta específica
   * GET /api/usuarios-gestion/?cuenta_id=X
   */
  getUsuarios: async (cuentaId = null) => {
    try {
      const params = cuentaId ? { cuenta_id: cuentaId } : {};
      const response = await api.get('/usuarios-gestion/', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Obtiene las cuentas del usuario actual
   * GET /api/usuarios-gestion/mis_cuentas/
   */
  getMisCuentas: async () => {
    try {
      const response = await api.get('/usuarios-gestion/mis_cuentas/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Cambia el rol de un usuario (soporta user_cuenta_id para configuración por cuenta)
   * POST /api/usuarios-gestion/cambiar_rol/
   */
  cambiarRol: async (userId, tipoUsuarioId, userCuentaId = null) => {
    try {
      const payload = {
        tipo_usuario_id: tipoUsuarioId
      };

      if (userCuentaId) {
        payload.user_cuenta_id = userCuentaId;
      } else {
        payload.user_id = userId;
      }

      const response = await api.post('/usuarios-gestion/cambiar_rol/', payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Marca/desmarca un usuario como editor predeterminado de la cuenta
   * POST /api/usuarios-gestion/toggle_editor_predeterminado/
   */
  actualizarPantallas: async (userCuentaId, pantallas) => {
    try {
      const response = await api.post('/usuarios-gestion/actualizar_pantallas/', {
        user_cuenta_id: userCuentaId,
        pantallas
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  toggleEditorPredeterminado: async (userCuentaId, valor) => {
    try {
      const response = await api.post('/usuarios-gestion/toggle_editor_predeterminado/', {
        user_cuenta_id: userCuentaId,
        editor_predeterminado: valor
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Agrega un grupo de permisos a un usuario
   * POST /api/usuarios-gestion/gestionar_grupo/
   */
  agregarGrupo: async (userId, grupoId) => {
    try {
      const response = await api.post('/usuarios-gestion/gestionar_grupo/', {
        user_id: userId,
        grupo_id: grupoId,
        accion: 'agregar'
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Quita un grupo de permisos de un usuario
   * POST /api/usuarios-gestion/gestionar_grupo/
   */
  quitarGrupo: async (userId, grupoId) => {
    try {
      const response = await api.post('/usuarios-gestion/gestionar_grupo/', {
        user_id: userId,
        grupo_id: grupoId,
        accion: 'quitar'
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Actualiza todos los grupos de un usuario (soporta user_cuenta_id para config por cuenta)
   * POST /api/usuarios-gestion/actualizar_grupos/
   */
  actualizarGrupos: async (userId, gruposIds = [], userCuentaId = null) => {
    try {
      const payload = {
        grupos_ids: gruposIds
      };

      if (userCuentaId) {
        payload.user_cuenta_id = userCuentaId;
      } else {
        payload.user_id = userId;
      }

      const response = await api.post('/usuarios-gestion/actualizar_grupos/', payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Activa o desactiva un usuario
   * POST /api/usuarios-gestion/{id}/toggle_estado/
   */
  toggleEstado: async (userId) => {
    try {
      const response = await api.post(`/usuarios-gestion/${userId}/toggle_estado/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Obtiene los tipos de usuario (roles) disponibles
   * GET /api/usuarios-gestion/tipos_usuario/
   */
  getTiposUsuario: async () => {
    try {
      const response = await api.get('/usuarios-gestion/tipos_usuario/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Obtiene los grupos de permisos disponibles
   * GET /api/usuarios-gestion/grupos/
   */
  getGrupos: async () => {
    try {
      const response = await api.get('/usuarios-gestion/grupos/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Busca usuarios por texto
   * GET /api/usuarios-gestion/buscar/?q=texto
   */
  buscarUsuarios: async (query) => {
    try {
      const response = await api.get('/usuarios-gestion/buscar/', {
        params: { q: query }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Crea un nuevo usuario
   * POST /api/usuarios-gestion/
   */
  crearUsuario: async (datosUsuario) => {
    try {
      const response = await api.post('/usuarios-gestion/', datosUsuario);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Actualiza un usuario existente (soporta user_cuenta_id para config por cuenta)
   * PATCH /api/usuarios-gestion/{id}/
   */
  actualizarUsuario: async (userId, datosUsuario, userCuentaId = null) => {
    try {
      const payload = { ...datosUsuario };
      if (userCuentaId) {
        payload.user_cuenta_id = userCuentaId;
      }
      const response = await api.patch(`/usuarios-gestion/${userId}/`, payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Actualiza las etiquetas permitidas de un usuario
   * POST /api/usuarios-gestion/actualizar_etiquetas/
   */
  actualizarEtiquetas: async (userId, etiquetas = [], userCuentaId = null) => {
    try {
      const payload = { etiquetas };
      if (userCuentaId) {
        payload.user_cuenta_id = userCuentaId;
      } else {
        payload.user_id = userId;
      }
      const response = await api.post('/usuarios-gestion/actualizar_etiquetas/', payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Obtiene las etiquetas disponibles (usadas en ContentPost de la cuenta)
   * GET /api/usuarios-gestion/etiquetas_disponibles/
   */
  getEtiquetasDisponibles: async () => {
    try {
      const response = await api.get('/usuarios-gestion/etiquetas_disponibles/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // ===== Maestro de etiquetas (Configuración → Sistema → Etiquetas) =====
  getEtiquetas: async () => {
    try {
      const response = await api.get('/etiquetas/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  crearEtiqueta: async (data) => {
    try {
      const response = await api.post('/etiquetas/', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  actualizarEtiqueta: async (id, data) => {
    try {
      const response = await api.patch(`/etiquetas/${id}/`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  eliminarEtiqueta: async (id) => {
    try {
      await api.delete(`/etiquetas/${id}/`);
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Cambia la contraseña de un usuario (solo superuser)
   * POST /api/usuarios-gestion/{id}/cambiar_password/
   */
  cambiarPassword: async (userId, nuevaPassword, confirmarPassword) => {
    try {
      const response = await api.post(`/usuarios-gestion/${userId}/cambiar_password/`, {
        nueva_password: nuevaPassword,
        confirmar_password: confirmarPassword,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

export default usuariosService;
